/**
 * A Cache-API-backed store that survives a per-entry ceiling where one exists.
 *
 * THE MEASUREMENT, taken 2026-07-26 in Chromium 148 (Electron 42) on Windows,
 * by calling cache.put() directly with synthetic bodies:
 *
 *     268,435,455 bytes (256 MiB - 1)   put() resolves, match() returns it
 *     268,435,456 bytes (256 MiB)       put() REJECTS
 *     "UnknownError: Failed to execute 'put' on 'Cache': Unexpected internal error."
 *
 * Exact to the byte, and not quota pressure: the origin had 509 MB used of a
 * 5.96 GB quota at the time.
 *
 * THE CEILING TRACKS THE STORAGE BUCKET, NOT THE BROWSER. Three runs, one
 * machine, one origin:
 *
 *     Chromium 148 (Electron 42)   FAILS at 256 MiB      quota  5.96 GB
 *     Chrome 150, normal profile   no ceiling to 4 GiB   quota 12.99 GB
 *     Chrome 150, INCOGNITO        FAILS at 256 MiB      quota  6.44 GB
 *
 * The same Chrome binary does both, so this is not a build difference and not
 * an Electron difference. Those were the first two theories and the incognito
 * run killed them in one line.
 *
 * What the failing cases share is a roughly 6 GB quota against the permissive
 * case's 13. Whether the per-entry cap is derived from the quota, or both come
 * from something else about an ephemeral or partitioned bucket, is NOT
 * established. Note the pane persists weights across reloads, so "incognito is
 * memory-backed" does not explain it on its own.
 *
 * WHY THIS MATTERS MORE THAN THE NUMBER. Incognito is a first-class way to try
 * a website, and this bay's entire pitch is that nothing leaves the tab. The
 * privacy-minded visitor it is aimed at is exactly the one likely to open it in
 * a private window, where every model above 256 MiB fails to cache and silently
 * re-downloads on every visit. The chunking is not legacy insurance for an old
 * Electron build. It is required for a visitor we should expect.
 *
 * CHUNKING STAYS. Reasons, in order of weight:
 *
 *   1. A PRIVATE WINDOW hits the ceiling on the same browser that does not hit
 *      it otherwise, so this is a live case rather than a legacy one.
 *   2. A visitor's bucket is unknowable in advance and the failure is SILENT:
 *      transformers.js logs a warning and the model re-downloads forever.
 *   3. Chrome's round-trip throughput DEGRADES with entry size, 425 MB/s at
 *      1 GiB against 223 MB/s at 4 GiB, so one enormous entry is not a free win
 *      over several smaller ones.
 *   4. It costs a manifest lookup on a path that is already IO-bound.
 *
 * Worth knowing for the catalogue: a normal Chrome profile would cache Qwen
 * 4B's 2.07 GB decoder as a single entry. The same browser in a private window
 * would not cache it at all without this file.
 *
 * WHY IT LOOKED SILENT. transformers.js does catch the rejection, at
 * utils/hub.js, but only as `logger.warn('Unable to add response to browser
 * cache: ...')`. Watching the console for errors rather than warnings hides
 * it completely, and the model simply re-downloads on every visit with no
 * outward sign.
 *
 * WHAT IT COST. The default model's weights are a single 469 MB
 * `model_q4f16.onnx_data`, so nothing about the shipped configuration could
 * ever be cached. embeddinggemma's 175 MB file cached fine, which is exactly
 * why the failure was confusing: caching plainly worked, for some files.
 *
 * THE FIX. Split oversized bodies into sub-limit chunks under synthetic keys
 * and write a manifest last, so a partial write can never be mistaken for a
 * complete one. Small bodies take an untouched fast path, because the 99%
 * case should not pay for the exception.
 *
 * 64 MiB is correct under any ceiling at or above it, and the chunk size is a
 * constructor parameter for the day some browser proves stricter. Firefox and
 * Safari remain unmeasured.
 */

/**
 * The ceiling measured in Chromium 148 / Electron 42.
 *
 * Recorded for provenance and NOT used as the chunk size, which matters more
 * now that it is known to be build-specific: real Chrome 150 stored 320 MiB
 * without complaint. Treat this as one browser's number, never as the API's.
 */
export const MEASURED_ENTRY_LIMIT = 256 * 1024 * 1024;

/**
 * 64 MiB. Four times under the measured ceiling, which is enough headroom for
 * a browser that is merely stricter rather than wildly different, while
 * keeping the default model to 8 chunks rather than dozens.
 */
export const DEFAULT_CHUNK_BYTES = 64 * 1024 * 1024;

// `CHUNK_ABOVE` used to live here, exported, referenced nowhere, documented as
// "bodies at or above this get chunked". That was never true: `put()` splits on
// the INSTANCE's `chunkBytes`, so a cache constructed with a different chunk
// size would have chunked at a threshold the constant denied. A constant that
// describes behaviour the code does not have is worse than no constant.

/**
 * Exported so a reader of the cache can tell the three kinds of entry apart:
 * an ordinary small body, a manifest, and the chunks a manifest owns. Counting
 * bytes without that distinction reported a 764 MB model as "cached 21 MB",
 * because the chunks carry no content-length and only the config files counted.
 */
export const MANIFEST_HEADER = "x-mind-chunked";
export const CHUNK_ORIGIN = "https://mind-chunk.invalid";

/** The slice of the Cache API this needs. Injectable so it can be tested without a browser. */
export interface CacheLike {
  match(request: string): Promise<Response | undefined>;
  put(request: string, response: Response): Promise<void>;
  delete(request: string): Promise<boolean>;
}

type Manifest = {
  v: 1;
  size: number;
  chunks: number;
  chunkBytes: number;
  headers: [string, string][];
};

/**
 * Synthetic key for chunk i.
 *
 * A separate origin rather than a fragment or query on the real URL: the
 * Cache API strips fragments when matching, and reusing the real URL's query
 * string risks colliding with a genuine parameter. This namespace cannot
 * collide with anything real.
 */
const chunkKey = (url: string, i: number) => `${CHUNK_ORIGIN}/${encodeURIComponent(url)}/${i}`;

export type ProgressFn = (data: { progress: number; loaded: number; total: number }) => void;

export class ChunkedCache {
  // Declared as fields and assigned in the body, NOT as constructor parameter
  // properties. Parameter properties are not erasable syntax, so Node's
  // --experimental-strip-types refuses the file and the headless tests cannot
  // import it. Same constraint as the pure modules.
  private backing: CacheLike;
  private chunkBytes: number;

  constructor(backing: CacheLike, chunkBytes: number = DEFAULT_CHUNK_BYTES) {
    this.backing = backing;
    this.chunkBytes = chunkBytes;
  }

  static async open(name: string, chunkBytes: number = DEFAULT_CHUNK_BYTES): Promise<ChunkedCache> {
    const c = await caches.open(name);
    return new ChunkedCache(
      {
        match: (r) => c.match(r),
        put: (r, res) => c.put(r, res),
        delete: (r) => c.delete(r),
      },
      chunkBytes,
    );
  }

  async match(request: string): Promise<Response | undefined> {
    const head = await this.backing.match(request);
    if (!head) return undefined;

    // Fast path: an ordinary entry written by the small-body branch, or one
    // left behind by the stock cache before this existed.
    if (head.headers.get(MANIFEST_HEADER) !== "1") return head;

    let meta: Manifest;
    try {
      meta = (await head.json()) as Manifest;
    } catch {
      await this.delete(request);
      return undefined;
    }

    const out = new Uint8Array(meta.size);
    let offset = 0;
    for (let i = 0; i < meta.chunks; i++) {
      const part = await this.backing.match(chunkKey(request, i));
      if (!part) {
        // A chunk is missing, so the whole entry is unusable. Clear it and
        // report a miss; the caller re-downloads and rewrites cleanly.
        await this.delete(request);
        return undefined;
      }
      const buf = new Uint8Array(await part.arrayBuffer());
      if (offset + buf.length > out.length) {
        await this.delete(request);
        return undefined;
      }
      out.set(buf, offset);
      offset += buf.length;
    }
    if (offset !== meta.size) {
      await this.delete(request);
      return undefined;
    }

    return new Response(out, { headers: new Headers(meta.headers) });
  }

  async put(request: string, response: Response, onProgress?: ProgressFn): Promise<void> {
    const buf = new Uint8Array(await response.arrayBuffer());
    const total = buf.byteLength;

    // Preserve the response's headers. content-length especially: the Cache
    // API can strip it, and transformers.js relies on it for progress.
    const headers: [string, string][] = [];
    response.headers.forEach((v, k) => {
      if (k.toLowerCase() !== MANIFEST_HEADER) headers.push([k, v]);
    });
    if (!response.headers.has("content-length")) headers.push(["content-length", String(total)]);

    if (total < this.chunkBytes) {
      await this.backing.put(request, new Response(buf, { headers: new Headers(headers) }));
      onProgress?.({ progress: 100, loaded: total, total });
      return;
    }

    const chunks = Math.ceil(total / this.chunkBytes);
    const written: string[] = [];
    try {
      for (let i = 0; i < chunks; i++) {
        const start = i * this.chunkBytes;
        const slice = buf.subarray(start, Math.min(start + this.chunkBytes, total));
        const key = chunkKey(request, i);
        // Copy the slice: a subarray shares the parent buffer, and some
        // implementations retain the whole thing rather than the view.
        await this.backing.put(key, new Response(new Uint8Array(slice)));
        written.push(key);
        const loaded = Math.min(start + slice.length, total);
        onProgress?.({ progress: (loaded / total) * 100, loaded, total });
      }

      // The manifest goes LAST, on purpose. Until it exists, match() sees
      // nothing and the orphaned chunks are inert. There is no window in
      // which a half-written entry looks complete.
      const meta: Manifest = { v: 1, size: total, chunks, chunkBytes: this.chunkBytes, headers };
      await this.backing.put(
        request,
        new Response(JSON.stringify(meta), {
          headers: new Headers({ [MANIFEST_HEADER]: "1", "content-type": "application/json" }),
        }),
      );
    } catch (err) {
      // Roll back so orphaned chunks do not sit in the quota forever.
      for (const key of written) {
        try {
          await this.backing.delete(key);
        } catch {
          /* best effort */
        }
      }
      throw err;
    }
  }

  async delete(request: string): Promise<boolean> {
    const head = await this.backing.match(request);
    if (head && head.headers.get(MANIFEST_HEADER) === "1") {
      try {
        const meta = (await head.json()) as Manifest;
        for (let i = 0; i < meta.chunks; i++) await this.backing.delete(chunkKey(request, i));
      } catch {
        /* manifest unreadable; drop the head anyway */
      }
    }
    return this.backing.delete(request);
  }
}
